import { useState } from "react";
import axios from "axios";
import { API } from "../App";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import { CheckCircle, XCircle, ClipboardCheck, Send, AlertTriangle } from "lucide-react";
import { toast } from "sonner";

export default function ChecklistModal({ isOpen, onClose, timeRecordId, onComplete }) {
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    workOk: null,
    workIssue: "",
    materialMissing: null,
    materialMissingDesc: "",
    areasCleaned: null,
    areasNotCleaned: ""
  });

  const canSubmit = 
    formData.workOk !== null &&
    formData.materialMissing !== null &&
    formData.areasCleaned !== null &&
    (formData.workOk || formData.workIssue.trim()) &&
    (!formData.materialMissing || formData.materialMissingDesc.trim()) &&
    (formData.areasCleaned || formData.areasNotCleaned.trim());

  const handleSubmit = async () => {
    if (!canSubmit) {
      toast.error("Por favor, responda todas as perguntas");
      return;
    }

    setLoading(true);
    try {
      await axios.post(`${API}/checklists`, {
        time_record_id: timeRecordId,
        work_ok: formData.workOk,
        work_issue: formData.workOk ? null : formData.workIssue,
        material_missing: formData.materialMissing,
        material_missing_desc: formData.materialMissing ? formData.materialMissingDesc : null,
        areas_cleaned: formData.areasCleaned,
        areas_not_cleaned: formData.areasCleaned ? null : formData.areasNotCleaned
      });

      toast.success("Checklist enviado com sucesso!");
      onComplete();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Erro ao enviar checklist");
    } finally {
      setLoading(false);
    }
  };

  const QuestionOption = ({ selected, onSelect, isYes, label }) => (
    <Button
      type="button"
      variant="outline"
      className={`flex-1 h-12 gap-2 transition-all ${
        selected === isYes
          ? isYes
            ? "bg-green-100 border-green-500 text-green-700 hover:bg-green-100"
            : "bg-amber-100 border-amber-500 text-amber-700 hover:bg-amber-100"
          : "border-gray-200 text-gray-500 hover:bg-gray-50"
      }`}
      onClick={() => onSelect(isYes)}
    >
      {isYes ? (
        <CheckCircle size={18} className={selected === true ? "text-green-600" : ""} />
      ) : (
        <XCircle size={18} className={selected === false ? "text-amber-600" : ""} />
      )}
      {label}
    </Button>
  );

  return (
    <Dialog open={isOpen} onOpenChange={() => {}}>
      <DialogContent className="bg-white border-gray-200 max-w-md mx-auto max-h-[90vh] overflow-y-auto" hideCloseButton>
        <DialogHeader>
          <div className="flex items-center justify-center gap-3 mb-2">
            <div className="w-12 h-12 rounded-full bg-gradient-to-r from-[#2ECC40] to-[#F5A623] flex items-center justify-center">
              <ClipboardCheck className="w-6 h-6 text-white" />
            </div>
          </div>
          <DialogTitle className="text-center text-xl text-gray-800">
            Checklist de Saída
          </DialogTitle>
          <p className="text-center text-gray-500 text-sm mt-1">
            Por favor, responda às perguntas abaixo antes de finalizar
          </p>
        </DialogHeader>

        <div className="space-y-6 mt-4">
          {/* Question 1: Work OK */}
          <div className="space-y-3">
            <Label className="text-gray-700 font-medium">
              1. O trabalho decorreu sem problemas?
            </Label>
            <div className="flex gap-3">
              <QuestionOption
                selected={formData.workOk}
                onSelect={(val) => setFormData({ ...formData, workOk: val })}
                isYes={true}
                label="Sim"
              />
              <QuestionOption
                selected={formData.workOk}
                onSelect={(val) => setFormData({ ...formData, workOk: val })}
                isYes={false}
                label="Não"
              />
            </div>
            {formData.workOk === false && (
              <div className="animate-fade-in">
                <div className="flex items-center gap-2 text-amber-600 text-sm mb-2">
                  <AlertTriangle size={14} />
                  Por favor, descreva o motivo:
                </div>
                <Textarea
                  value={formData.workIssue}
                  onChange={(e) => setFormData({ ...formData, workIssue: e.target.value })}
                  placeholder="Descreva o problema encontrado..."
                  className="bg-gray-50 border-gray-200 text-gray-800 min-h-[80px]"
                  data-testid="work-issue-input"
                />
              </div>
            )}
          </div>

          {/* Question 2: Material Missing */}
          <div className="space-y-3">
            <Label className="text-gray-700 font-medium">
              2. Falta algum material de consumo no local?
            </Label>
            <div className="flex gap-3">
              <QuestionOption
                selected={formData.materialMissing}
                onSelect={(val) => setFormData({ ...formData, materialMissing: val })}
                isYes={false}
                label="Não"
              />
              <QuestionOption
                selected={formData.materialMissing}
                onSelect={(val) => setFormData({ ...formData, materialMissing: val })}
                isYes={true}
                label="Sim"
              />
            </div>
            {formData.materialMissing === true && (
              <div className="animate-fade-in">
                <div className="flex items-center gap-2 text-amber-600 text-sm mb-2">
                  <AlertTriangle size={14} />
                  Indique qual material está em falta:
                </div>
                <Textarea
                  value={formData.materialMissingDesc}
                  onChange={(e) => setFormData({ ...formData, materialMissingDesc: e.target.value })}
                  placeholder="Ex: Papel higiênico, detergente, etc..."
                  className="bg-gray-50 border-gray-200 text-gray-800 min-h-[80px]"
                  data-testid="material-missing-input"
                />
              </div>
            )}
          </div>

          {/* Question 3: Areas Cleaned */}
          <div className="space-y-3">
            <Label className="text-gray-700 font-medium">
              3. Todos os ambientes previstos foram limpos?
            </Label>
            <div className="flex gap-3">
              <QuestionOption
                selected={formData.areasCleaned}
                onSelect={(val) => setFormData({ ...formData, areasCleaned: val })}
                isYes={true}
                label="Sim"
              />
              <QuestionOption
                selected={formData.areasCleaned}
                onSelect={(val) => setFormData({ ...formData, areasCleaned: val })}
                isYes={false}
                label="Não"
              />
            </div>
            {formData.areasCleaned === false && (
              <div className="animate-fade-in">
                <div className="flex items-center gap-2 text-amber-600 text-sm mb-2">
                  <AlertTriangle size={14} />
                  Indique quais áreas ficaram por limpar:
                </div>
                <Textarea
                  value={formData.areasNotCleaned}
                  onChange={(e) => setFormData({ ...formData, areasNotCleaned: e.target.value })}
                  placeholder="Ex: Banheiro do 2º andar, cozinha..."
                  className="bg-gray-50 border-gray-200 text-gray-800 min-h-[80px]"
                  data-testid="areas-not-cleaned-input"
                />
              </div>
            )}
          </div>

          {/* Submit Button */}
          <Button
            onClick={handleSubmit}
            disabled={!canSubmit || loading}
            className="w-full h-14 btn-primary text-white font-bold text-lg"
            data-testid="submit-checklist-btn"
          >
            {loading ? (
              <div className="spinner w-6 h-6 border-2 border-white border-t-transparent"></div>
            ) : (
              <>
                <Send className="mr-2 h-5 w-5" />
                Enviar Checklist
              </>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Thank you modal shown after checklist submission
export function ThankYouModal({ isOpen, onClose }) {
  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-white border-gray-200 max-w-sm mx-auto text-center">
        <div className="py-6">
          <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-r from-[#2ECC40] to-[#F5A623] flex items-center justify-center mb-6">
            <CheckCircle className="w-10 h-10 text-white" />
          </div>
          <h2 className="text-2xl font-bold text-gray-800 mb-3">
            Obrigado!
          </h2>
          <p className="text-gray-600 text-lg">
            Obrigado pela sua colaboração.
          </p>
          <p className="text-[#F5A623] font-semibold text-lg mt-2">
            Juntos somos mais.
          </p>
          <Button
            onClick={onClose}
            className="mt-6 btn-primary text-white font-semibold px-8"
            data-testid="thank-you-close-btn"
          >
            Continuar
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
